import ReactApexChart from "react-apexcharts";
import { useMemo } from "react";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const currentMonthIndex = new Date().getMonth(); // 0-based

function EChart({ data = [], totalMembers = 0 }) {
  // Highlight current month bar with a different colour
  const barColors = useMemo(() =>
    MONTHS.map((_, i) =>
      i === currentMonthIndex
        ? "#1890ff"          // current month — vivid blue
        : "rgba(24,144,255,0.55)" // other months — lighter
    ), []);

  const maxVal  = Math.max(...(data || []), 1);
  const avgVal  = data?.length ? Math.round(data.reduce((a, b) => a + b, 0) / data.length) : 0;
  const peakIdx = data?.indexOf(maxVal) ?? -1;

  const series = [{ name: "New Members", data: data || [] }];

  const options = {
    chart: {
      type: "bar",
      height: 240,
      toolbar: { show: false },
      animations: { enabled: true, speed: 600 },
      background: "transparent",
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "52%",
        borderRadius: 6,
        distributed: true,   // per-bar colour from barColors
      },
    },
    colors: barColors,
    dataLabels: {
      enabled: true,
      formatter: (val) => (val > 0 ? val : ""),
      offsetY: -6,
      style: { fontSize: "11px", colors: ["#555"], fontWeight: 600 },
    },
    stroke: { show: false },
    grid: {
      borderColor: "#f0f0f0",
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
    },
    xaxis: {
      categories: MONTHS,
      labels: {
        style: { colors: "#8c8c8c", fontSize: "12px", fontWeight: 500 },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: { colors: "#8c8c8c", fontSize: "12px" },
        formatter: (v) => Math.round(v),
      },
    },
    // Avg reference line
    annotations: {
      yaxis: avgVal > 0 ? [{
        y: avgVal,
        borderColor: "#faad14",
        borderWidth: 1.5,
        strokeDashArray: 4,
        label: {
          text: `Avg ${avgVal}`,
          style: { color: "#faad14", background: "#fffbe6", fontSize: "11px", fontWeight: 600 },
          position: "right",
          offsetX: -10,
        },
      }] : [],
      points: peakIdx >= 0 && maxVal > 0 ? [{
        x: MONTHS[peakIdx],
        y: maxVal,
        marker: { size: 5, fillColor: "#1890ff", strokeColor: "#fff", strokeWidth: 2 },
        label: {
          text: "Peak",
          style: { color: "#fff", background: "#1890ff", fontSize: "10px" },
          offsetY: -8,
        },
      }] : [],
    },
    legend: { show: false },
    tooltip: {
      theme: "light",
      y: {
        formatter: (val) => `${val} members`,
        title: { formatter: () => "" },
      },
      x: { show: true },
    },
    states: {
      hover: { filter: { type: "lighten", value: 0.1 } },
    },
  };

  return (
    <div>
      {/* Summary row */}
      {totalMembers > 0 && (
        <div style={{ display: "flex", gap: "24px", marginBottom: "8px", flexWrap: "wrap" }}>
          <div>
            <span style={{ fontSize: "11px", color: "#8c8c8c" }}>Total (Active)</span>
            <div style={{ fontSize: "18px", fontWeight: 700, color: "#1890ff" }}>
              {Number(totalMembers).toLocaleString("en-IN")}
            </div>
          </div>
          {peakIdx >= 0 && maxVal > 0 && (
            <div>
              <span style={{ fontSize: "11px", color: "#8c8c8c" }}>Peak Month</span>
              <div style={{ fontSize: "18px", fontWeight: 700, color: "#52c41a" }}>
                {MONTHS[peakIdx]} ({maxVal})
              </div>
            </div>
          )}
          {avgVal > 0 && (
            <div>
              <span style={{ fontSize: "11px", color: "#8c8c8c" }}>Monthly Avg</span>
              <div style={{ fontSize: "18px", fontWeight: 700, color: "#faad14" }}>{avgVal}</div>
            </div>
          )}
        </div>
      )}
      <ReactApexChart options={options} series={series} type="bar" height={220} />
    </div>
  );
}

export default EChart;
